/**
 * Two fingers on a touchscreen: pan or pinch (SRS C2.1).
 *
 * Fingers are never perfectly steady, so a gesture that re-decides every frame
 * flickers between panning and zooming and feels broken. The rule is therefore:
 * gather evidence for 80 ms, decide once, and hold that decision until the fingers
 * lift. A gesture never changes its mind halfway through.
 *
 * Touchpads are not handled here — the browser reports those as wheel events, which
 * step 2.5 already reads.
 */

import type { Point } from '@opal/shared';

/** SRS C2.1 — how long the gesture watches before it commits. */
export const DECISION_WINDOW_MS = 80;

/** Below this much change in finger spacing, a wobble is not a pinch. */
const MIN_PINCH_PX = 8;

/** How much more the spacing must change than the hand moves, to count as a pinch. */
const PINCH_BIAS = 0.6;

export type GestureKind = 'pan' | 'zoom';

export interface GestureEvidence {
  readonly startDistance: number;
  readonly distance: number;
  readonly startCentre: Point;
  readonly centre: Point;
}

export function distanceBetween(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function centreOf(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Pan or zoom.
 *
 * Two fingers moving together keep their spacing and shift their middle; two fingers
 * pinching change their spacing and barely shift their middle. Comparing the two is
 * what tells them apart, and the bias keeps an ordinary drag from being read as a
 * pinch when a finger drifts slightly.
 */
export function decideGesture(evidence: GestureEvidence): GestureKind {
  const spread = Math.abs(evidence.distance - evidence.startDistance);
  const moved = distanceBetween(evidence.startCentre, evidence.centre);
  if (spread < MIN_PINCH_PX) return 'pan';
  return spread > moved * PINCH_BIAS ? 'zoom' : 'pan';
}

export interface TouchGestureHandlers {
  /** Drag the paper by this many screen pixels. */
  readonly onPan: (dxPx: number, dyPx: number) => void;
  /** Multiply the zoom, keeping the point between the fingers where it is. */
  readonly onZoom: (factor: number, anchor: Point) => void;
  /** A second finger landed: whatever the first one was drawing was not meant. */
  readonly onStart: () => void;
  readonly onEnd: () => void;
}

export interface TouchGestures {
  readonly detach: () => void;
  /** True while two fingers are down, so the pen stays out of the way. */
  readonly active: () => boolean;
}

/** Where the fingers are now. */
interface GestureMove {
  readonly distance: number;
  readonly centre: Point;
}

interface Gesture extends GestureMove {
  readonly startedAt: number;
  readonly startDistance: number;
  readonly startCentre: Point;
  kind: GestureKind | null;
  distance: number;
  centre: Point;
}

/** Screen position of a pointer, relative to the element. */
function positionIn(element: HTMLElement, event: PointerEvent): Point {
  const bounds = element.getBoundingClientRect();
  return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
}

/** The two live touches, or null while there are not exactly two to work with. */
function twoOf(touches: Map<number, Point>): [Point, Point] | null {
  const [first, second] = [...touches.values()];
  return first && second ? [first, second] : null;
}

/** Starts watching a two-finger gesture. Nothing is decided yet. */
function startGesture(pair: [Point, Point], now: number): Gesture {
  const [a, b] = pair;
  return {
    startedAt: now,
    startDistance: distanceBetween(a, b),
    startCentre: centreOf(a, b),
    kind: null,
    distance: distanceBetween(a, b),
    centre: centreOf(a, b),
  };
}

/**
 * Commits the gesture once 80 ms of evidence exists, and keeps that answer for the
 * rest of the gesture. Returns null while it is still watching.
 */
function commitGesture(gesture: Gesture, at: number, next: GestureMove): GestureKind | null {
  if (gesture.kind !== null) return gesture.kind;
  if (at - gesture.startedAt < DECISION_WINDOW_MS) return null;
  gesture.kind = decideGesture({
    startDistance: gesture.startDistance,
    distance: next.distance,
    startCentre: gesture.startCentre,
    centre: next.centre,
  });
  return gesture.kind;
}

/** Applies one two-finger move, once the gesture has committed to what it is. */
function applyGesture(
  gesture: Gesture,
  next: GestureMove,
  handlers: TouchGestureHandlers,
): void {
  if (gesture.kind === 'zoom') {
    handlers.onZoom(next.distance / (gesture.distance || next.distance), next.centre);
    return;
  }
  handlers.onPan(next.centre.x - gesture.centre.x, next.centre.y - gesture.centre.y);
}

/** Subscribes to the pointer events a gesture needs. Returns the unsubscribe. */
function onPointerEvents(
  element: HTMLElement,
  down: (event: PointerEvent) => void,
  move: (event: PointerEvent) => void,
  lift: (event: PointerEvent) => void,
): () => void {
  element.addEventListener('pointerdown', down);
  element.addEventListener('pointermove', move);
  element.addEventListener('pointerup', lift);
  element.addEventListener('pointercancel', lift);
  return () => {
    element.removeEventListener('pointerdown', down);
    element.removeEventListener('pointermove', move);
    element.removeEventListener('pointerup', lift);
    element.removeEventListener('pointercancel', lift);
  };
}

export function attachTouchGestures(
  element: HTMLElement,
  handlers: TouchGestureHandlers,
): TouchGestures {
  const touches = new Map<number, Point>();
  let gesture: Gesture | null = null;

  const down = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch') return;
    touches.set(event.pointerId, positionIn(element, event));
    const pair = twoOf(touches);
    if (touches.size !== 2 || !pair) return;
    gesture = startGesture(pair, event.timeStamp);
    handlers.onStart();
  };

  const move = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch' || !touches.has(event.pointerId)) return;
    touches.set(event.pointerId, positionIn(element, event));
    const pair = twoOf(touches);
    if (!gesture || !pair) return;

    const [a, b] = pair;
    const next: GestureMove = { distance: distanceBetween(a, b), centre: centreOf(a, b) };
    // SRS C2.1 — watch for 80 ms, then commit and never reconsider.
    if (commitGesture(gesture, event.timeStamp, next) !== null) {
      applyGesture(gesture, next, handlers);
    }
    gesture.distance = next.distance;
    gesture.centre = next.centre;
  };

  const lift = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch') return;
    touches.delete(event.pointerId);
    if (touches.size >= 2 || !gesture) return;
    gesture = null;
    handlers.onEnd();
  };

  const unsubscribe = onPointerEvents(element, down, move, lift);

  return {
    active: () => gesture !== null,
    detach() {
      unsubscribe();
      touches.clear();
      gesture = null;
    },
  };
}
